import { useState, useCallback, useMemo, useEffect } from "react";
import styles from "./AirportManager.module.css";
import AddEditAirportModal from "../../../components/GeoData/Airports/AddEditAirportModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import SearchableSelect from "../../../components/BaseComponents/SearchableSelect/SearchableSelect";
import { useToast } from "../../../context/ToastContext";
import {
  useGetAirports,
  useCreateAirport,
  useUpdateAirport,
  useDeleteAirport,
} from "../../../api/geo/useAirports";
import { useGetCountries } from "../../../api/geo/useCountries";

// Pending toggle changes: { [rowId]: { active? } }
const getDisplayValue = (row, field, pendingChanges) =>
  pendingChanges[row.id]?.[field] ?? row[field];

// The list is not airports alone — the HotelBeds sync also brings in train
// stations, ports and bus stations. "B" is what the sync stored for bus.
const TYPE_LABELS = {
  APT: "Airport",
  TRN: "Train Station",
  PRT: "Port",
  B: "Bus Station",
};

const TYPE_OPTIONS = [
  { label: "Type: All", value: "" },
  { label: "Airports", value: "APT" },
  { label: "Train Stations", value: "TRN" },
  { label: "Ports", value: "PRT" },
  { label: "Bus Stations", value: "B" },
];

// What the counts and empty state should call the rows. With no type chosen
// the list is a mix, so it stays deliberately generic.
const TYPE_NOUN = {
  "": "entries",
  APT: "airports",
  TRN: "train stations",
  PRT: "ports",
  B: "bus stations",
};

const AirportManager = () => {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [showAddEdit, setShowAddEdit] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [toggleSaveLoading, setToggleSaveLoading] = useState(false);

  const { data: airportsData, pagination, loading, error, refetch } = useGetAirports({
    page,
    limit,
    search: search || undefined,
    countryId: countryFilter || undefined,
    type: typeFilter || undefined,
    active: activeFilter,
    sortBy: "name",
    sortOrder: "ASC",
  });

  // Every country, not the first page: there are ~250, and a 200 cap silently dropped the end of
  // the alphabet (Zweden, Zwitserland, Zuid-Afrika, …) from the filter.
  const { data: countriesData } = useGetCountries({
    page: 1,
    limit: 500,
    sortBy: "name",
    sortOrder: "ASC",
  });

  const { execute: createAirport, loading: createLoading } = useCreateAirport();
  const { execute: updateAirport, loading: updateLoading } = useUpdateAirport();
  const { execute: deleteAirport, loading: deleteLoading } = useDeleteAirport();

  const rows = airportsData || [];
  const countries = (countriesData || []).map((c) => ({ id: c.id, name: c.name }));
  const saveLoading = createLoading || updateLoading;
  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const applyToggle = useCallback((row, field) => {
    setPendingChanges((prev) => {
      const current = prev[row.id] ?? {};
      const serverVal = row[field];
      const pendingVal = current[field];
      const nextVal = pendingVal !== undefined ? !pendingVal : !serverVal;
      return {
        ...prev,
        [row.id]: { ...current, [field]: nextVal },
      };
    });
  }, []);

  // Auto-search: debounce the input so the list filters as you type (no button).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
      setPendingChanges({});
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowAddEdit(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowAddEdit(true);
  };

  const handleDeleteClick = (row) => setRowToDelete(row);

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteAirport(rowToDelete.id);
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[rowToDelete.id];
        return next;
      });
      refetch();
      setRowToDelete(null);
      showToast("Airport deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete airport", "error");
    }
  };

  const handleSaveToggles = async () => {
    const ids = Object.keys(pendingChanges);
    if (ids.length === 0) return;
    setToggleSaveLoading(true);
    try {
      for (const id of ids) {
        const patch = pendingChanges[id];
        const row = rows.find((r) => String(r.id) === String(id));
        const payload = { active: patch.active ?? row?.active };
        await updateAirport(id, payload);
      }
      setPendingChanges({});
      refetch();
      showToast("Changes saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save changes", "error");
    } finally {
      setToggleSaveLoading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        await createAirport(payload);
        showToast("Airport created successfully", "success");
      } else if (selectedRow?.id) {
        await updateAirport(selectedRow.id, payload);
        showToast("Airport updated successfully", "success");
      }
      setShowAddEdit(false);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save airport";
      showToast(msg, "error");
      throw new Error(msg);
    }
  };

  // Show only the linked CITY names in the list (the stored JSON also carries
  // each city's zones, which must not leak into the column).
  const linkedCitiesDisplay = (row) => {
    const v = row.linkedCities;
    if (v == null || v === "") return "—";

    // "Istanbul (IST)" once the city's code is stored (saved from the Edit Airport modal).
    const toNames = (arr) =>
      arr
        .map((c) => (typeof c === "object" && c !== null ? (c.name && c.code ? `${c.name} (${c.code})` : c.name) : c))
        .filter(Boolean)
        .join(", ");

    if (Array.isArray(v)) return toNames(v) || "—";

    if (typeof v === "string") {
      const t = v.trim();
      if (t.startsWith("[")) {
        try {
          const arr = JSON.parse(t);
          if (Array.isArray(arr)) return toNames(arr) || "—";
        } catch {
          /* fall through to legacy comma-separated names */
        }
      }
      return t;
    }

    return String(v);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Airports</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Airport
        </button>
      </div>

      <div className={styles.tagLine}>
        <span className={styles.tagIcon} aria-hidden>ℹ</span>
        <span>Airports are used for location and search. IATA and ICAO codes help travelers find the right airport.</span>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search airport, IATA or ICAO..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <SearchableSelect
          className={styles.dropdownFilter}
          width={240}
          placeholder="Country: All"
          searchPlaceholder="Search country..."
          value={countryFilter}
          options={[
            { label: "Country: All", value: "" },
            ...countries.map((c) => ({ label: c.name, value: String(c.id) })),
          ]}
          onChange={(value) => {
            setCountryFilter(value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        {/* Same dropdown as the country filter; no search box for a handful of options. */}
        <SearchableSelect
          className={styles.dropdownFilter}
          width={180}
          searchable={false}
          placeholder="Type: All"
          value={typeFilter}
          options={TYPE_OPTIONS}
          onChange={(value) => {
            setTypeFilter(value);
            setPage(1);
            setPendingChanges({});
          }}
        />
        <SearchableSelect
          className={styles.dropdownFilter}
          width={160}
          searchable={false}
          placeholder="Status: All"
          value={activeFilter}
          options={[
            { label: "Status: All", value: "" },
            { label: "Active", value: "true" },
            { label: "Inactive", value: "false" },
          ]}
          onChange={(value) => {
            setActiveFilter(value);
            setPage(1);
            setPendingChanges({});
          }}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading airports…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {rows.length} of {pagination?.total ?? 0} {TYPE_NOUN[typeFilter] ?? "entries"}
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>IATA</th>
                    <th>Type</th>
                    <th>Country</th>
                    <th>Linked Cities</th>
                    <th>Lat</th>
                    <th>Lng</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={styles.noResults}>
                        No {TYPE_NOUN[typeFilter] ?? "entries"} found.
                      </td>
                    </tr>
                  ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.name}</td>
                      <td className={styles.code}>{row.code}</td>
                      <td>{TYPE_LABELS[row.type] ?? row.type ?? "—"}</td>
                      <td>{row.countryName ?? "—"}</td>
                      <td>{linkedCitiesDisplay(row)}</td>
                      <td>{row.latitude ?? "—"}</td>
                      <td>{row.longitude ?? "—"}</td>
                      <td>
                        <ToggleSwitch
                          checked={!!getDisplayValue(row, "active", pendingChanges)}
                          onChange={() => applyToggle(row, "active")}
                        />
                      </td>
                      <td className={styles.rowActions}>
                        <button onClick={() => handleEdit(row)}>Edit</button>
                        <button onClick={() => handleDeleteClick(row)}>Delete</button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>

            <div className={styles.footerBar}>
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Page {pagination?.page ?? page} of {pagination?.totalPages || 1} ({pagination?.total ?? 0} total)
                </span>
                {(pagination?.totalPages ?? 1) > 1 && (
                  <div className={styles.pageNumbers}>
                    {pageNumbers.map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
                      ) : (
                        <button
                          key={p}
                          className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                          onClick={() => {
                            setPage(p);
                            setPendingChanges({});
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className={styles.saveChangesBtn}
                onClick={handleSaveToggles}
                disabled={!hasPendingChanges || saveLoading}
              >
                {toggleSaveLoading ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddEditAirportModal
        open={showAddEdit}
        onClose={() => setShowAddEdit(false)}
        mode={mode}
        initialData={selectedRow}
        countries={countries}
        onSave={handleSave}
        saveLoading={saveLoading}
      />

      <DeleteConfirmModal
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        title="Delete airport"
        itemName={rowToDelete ? `${rowToDelete.name} (${rowToDelete.code})` : undefined}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AirportManager;
